import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as motion from 'motion/react-client';
import { AnimatePresence, useReducedMotion } from 'motion/react';
import ModalPortal from '../general/ModalPortal';
import Tooltip from '../general/Tooltip';
import { UserContext } from '../ContextProviders';
import { useNovaChat } from '@/hooks/useNovaChat';
import { useNova } from './NovaContext';
import NovaMessage from './NovaMessage';
import NovaComposer from './NovaComposer';
import NovaHistorySidebar from './NovaHistorySidebar';
import NovaUploadDialog from './NovaUploadDialog';
import NovaLogoMark from './NovaLogoMark';
import NovaBetaTag from './NovaBetaTag';
import NovaThemeToggle from './NovaThemeToggle';
import NovaThinkingIndicator from './NovaThinkingIndicator';
import NovaMark, { answerMarkState } from './NovaMark';
import { novaThemes } from './novaTheme';
import { NovaHistoryIcon, NovaNewChatIcon, NovaCloseIcon } from './novaIcons';

// sessionStorage key (per tab) for the active chat, so the page-level widget keeps it
// across navigation and refresh until the user starts a new one.
const NOVA_PANEL_CONVERSATION_STORAGE_KEY = 'nova-panel-conversation';

interface NovaPanelProps {
  // False on the standalone Nova pages, where the floating panel isn't the chat surface.
  persistSession?: boolean;
}

const NovaPanel: React.FC<NovaPanelProps> = ({ persistSession = true }) => {
  const { isOpen, closeNova, seedQuery, theme } = useNova();
  const user = useContext(UserContext);
  const {
    messages,
    suggestedQuestions,
    waiting,
    streaming,
    conversationId,
    conversations,
    conversationsLoading,
    published12mFilter,
    setPublished12mFilter,
    pendingFilePaths,
    addPendingFilePaths,
    removePendingFilePath,
    sendMessage,
    stopStreaming,
    startNewConversation,
    resumeConversation,
    loadConversations,
    deleteConversations,
  } = useNovaChat({ persistKey: persistSession ? NOVA_PANEL_CONVERSATION_STORAGE_KEY : undefined });
  const [view, setView] = useState<'chat' | 'history'>('chat');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadInitialFiles, setUploadInitialFiles] = useState<File[]>([]);
  const [hasDraft, setHasDraft] = useState(false);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  const prevMessageCountRef = useRef(messages.length);
  const seededRef = useRef(false);
  const surfaces = novaThemes[theme];
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (isOpen && seedQuery && !seededRef.current) {
      seededRef.current = true;
      sendMessage(seedQuery);
    }
    if (!isOpen) {
      seededRef.current = false;
      setView('chat');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, seedQuery]);

  useLayoutEffect(() => {
    // While streaming, messages update ~60 times a second — restarting a smooth
    // scroll on each one makes the transcript judder, so follow it instantly then.
    const el = transcriptRef.current;
    if (!el) return;
    const isNewTurn = messages.length !== prevMessageCountRef.current;
    prevMessageCountRef.current = messages.length;
    if (isNewTurn) stickToBottomRef.current = true;
    if (!stickToBottomRef.current) return;
    if (streaming) el.scrollTop = el.scrollHeight;
    else el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, streaming]);

  const handleTranscriptScroll = () => {
    const el = transcriptRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const openHistory = () => {
    setView('history');
    loadConversations();
  };

  const openUploadDialog = (files?: File[]) => {
    setUploadInitialFiles(files || []);
    setUploadOpen(true);
  };

  const iconButtonClass = 'h-[17px] w-[17px]';
  const chromeButtonClass =
    'flex h-[34px] w-[34px] cursor-pointer items-center justify-center rounded-[9px] transition-opacity hover:opacity-70';

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalPortal>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/10"
            style={{ zIndex: 999998 }}
            onClick={closeNova}
          />
          <motion.div
            initial={prefersReducedMotion ? { x: 0, opacity: 1 } : { x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={prefersReducedMotion ? { x: 0, opacity: 1 } : { x: 40, opacity: 0 }}
            transition={prefersReducedMotion ? { duration: 0 } : { type: 'spring', stiffness: 260, damping: 30 }}
            className="fixed right-2 left-2 flex flex-col overflow-hidden rounded-2xl shadow-2xl sm:right-4 sm:left-auto sm:w-[530px] sm:max-w-[92vw] sm:rounded-3xl"
            style={{
              zIndex: 999999,
              top: 'calc(var(--header-height) + 16px)',
              bottom: '16px',
              background: surfaces.surfaces.panelBase,
              color: surfaces.ink.body,
            }}
          >
            <div
              className="flex shrink-0 items-center gap-2 px-4 sm:gap-[14px] sm:pr-[22px] sm:pl-[26px]"
              style={{ height: 64, borderBottom: `1px solid ${surfaces.surfaces.hairline}` }}
            >
              <div className="flex items-center gap-[10px]">
                <NovaLogoMark theme={theme} />
                <NovaBetaTag theme={theme} />
              </div>
              <span className="flex-1" />

              <NovaThemeToggle segmentWidth={30} segmentHeight={26} iconSize={15} className="mr-2" />

              <div className="flex items-center gap-[2px]" style={{ color: surfaces.ink.chromeIcon }}>
                <Tooltip placement="bottom" content={view === 'chat' ? 'Chat history' : 'Back to chat'}>
                  <button
                    type="button"
                    aria-label={view === 'chat' ? 'Chat history' : 'Back to chat'}
                    onClick={() => (view === 'chat' ? openHistory() : setView('chat'))}
                    className={chromeButtonClass}
                  >
                    <NovaHistoryIcon className={iconButtonClass} />
                  </button>
                </Tooltip>
                <Tooltip placement="bottom" content="New chat">
                  <button type="button" aria-label="New chat" onClick={startNewConversation} className={chromeButtonClass}>
                    <NovaNewChatIcon className={iconButtonClass} />
                  </button>
                </Tooltip>
                <Tooltip placement="bottom" content="Close">
                  <button type="button" aria-label="Close Nova" onClick={closeNova} className={chromeButtonClass}>
                    <NovaCloseIcon className={iconButtonClass} />
                  </button>
                </Tooltip>
              </div>
            </div>

            {view === 'history' ? (
              <div className="flex min-h-0 flex-1 flex-col p-4">
                <div
                  className="mb-2 shrink-0 px-2 text-xs tracking-[0.08em] uppercase"
                  style={{ color: surfaces.ink.eyebrow }}
                >
                  Recent chats
                </div>
                <NovaHistorySidebar
                  conversations={conversations}
                  loading={conversationsLoading}
                  activeConversationId={conversationId}
                  onSelect={(id) => {
                    resumeConversation(id);
                    setView('chat');
                  }}
                  onDelete={(id) => deleteConversations([id])}
                  theme={theme}
                />
              </div>
            ) : (
              <>
                <div
                  ref={transcriptRef}
                  onScroll={handleTranscriptScroll}
                  className="flex-1 space-y-3 overflow-y-auto p-4"
                  data-lenis-prevent
                >
                  <span
                    className="flex items-center gap-1.5 text-[10px] font-medium tracking-[0.12em] uppercase"
                    style={{ color: surfaces.ink.secondary }}
                  >
                    Stylus Intelligence
                  </span>
                  {messages.length === 0 && (
                    <div className="flex flex-col items-start">
                      <div
                        className="w-fit max-w-[85%] rounded-2xl p-3 text-sm"
                        style={{ background: surfaces.surfaces.novaBubble }}
                      >
                        Hi, how can I help?
                      </div>
                      <NovaMark
                        state={hasDraft ? 'leanin' : 'breathing'}
                        theme={theme}
                        size={100}
                        className="mt-2"
                      />
                    </div>
                  )}
                  {messages.map((message, index) => {
                    const isLast = index === messages.length - 1;
                    return (
                      <NovaMessage
                        key={index}
                        message={message}
                        followUpQuestions={isLast ? suggestedQuestions : undefined}
                        onFollowUpClick={(question) => sendMessage(question)}
                        theme={theme}
                        isFirst={index === 0}
                        // The nearest preceding user turn, not just messages[index - 1] — a
                        // resumed conversation isn't guaranteed to alternate strictly.
                        searchTerm={
                          message.role === 'assistant'
                            ? messages.slice(0, index).findLast((m) => m.role === 'user')?.content
                            : undefined
                        }
                        published12mFilter={published12mFilter}
                      />
                    );
                  })}
                  {!waiting && messages[messages.length - 1]?.role === 'assistant' && (
                    <NovaMark state={answerMarkState({ streaming, leanIn: hasDraft })} theme={theme} size={100} />
                  )}
                  {waiting && <NovaThinkingIndicator theme={theme} published12mFilter={published12mFilter} markSize={100} />}
                </div>

                <div
                  className="shrink-0"
                  style={{ padding: '22px 24px 8px', borderTop: `1px solid ${surfaces.surfaces.hairline}` }}
                >
                  <NovaComposer
                    onSend={(query) => sendMessage(query)}
                    onAttach={openUploadDialog}
                    onAttachExistingDocument={(relativeName) => addPendingFilePaths([relativeName])}
                    userProfileId={user?.amsUserId}
                    waiting={waiting || streaming}
                    streaming={streaming}
                    onStop={stopStreaming}
                    hasMessages={messages.length > 0}
                    onDraftChange={(value) => setHasDraft(!!value)}
                    variant="compact"
                    theme={theme}
                    published12mFilter={published12mFilter}
                    onTogglePublished12mFilter={setPublished12mFilter}
                    pendingFilePaths={pendingFilePaths}
                    onRemovePendingFilePath={removePendingFilePath}
                  />
                </div>
              </>
            )}

            <NovaUploadDialog
              open={uploadOpen}
              userProfileId={user?.amsUserId || ''}
              initialFiles={uploadInitialFiles}
              onClose={() => setUploadOpen(false)}
              onUploaded={(filePaths) => {
                addPendingFilePaths(filePaths);
                setUploadOpen(false);
              }}
            />
          </motion.div>
        </ModalPortal>
      )}
    </AnimatePresence>
  );
};

export default NovaPanel;
